import { useContext, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { UserContext } from '../App';
import { GET_MY_PERMISSIONS } from '../graphql/queries/rbac';
import usePermissionsMode from '../components/Permissions/usePermissionsMode';

export const MY_PERMISSIONS_POLL_INTERVAL_MS = 60_000;

const TYPEOF_UNDEFINED = 'undefined';
const DOCUMENT_VISIBILITY_VISIBLE = 'visible';

function useMyPermissions() {
  const { currentOrgId } = useContext(UserContext);
  const { isRbacEnabled } = usePermissionsMode();
  const shouldSkip = !isRbacEnabled || !currentOrgId;

  const [isTabVisible, setIsTabVisible] = useState(() =>
    typeof document === TYPEOF_UNDEFINED ? true : document.visibilityState === DOCUMENT_VISIBILITY_VISIBLE
  );

  const { data, loading, error, refetch, networkStatus } = useQuery(GET_MY_PERMISSIONS, {
    skip: shouldSkip,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    pollInterval: shouldSkip || !isTabVisible ? 0 : MY_PERMISSIONS_POLL_INTERVAL_MS,
    notifyOnNetworkStatusChange: true
  });

  const callerPermissions = shouldSkip ? null : data?.getMyPermissions ?? null;
  const permissions = callerPermissions?.permissions ?? [];
  const roles = callerPermissions?.roles ?? [];
  const hasAccess = shouldSkip || permissions.length > 0;
  const isInitialLoading = !shouldSkip && loading && !data;
  const isRefetching = !shouldSkip && loading && !!data;

  useEffect(() => {
    const onVisibilityChange = () => {
      const visible = document.visibilityState === DOCUMENT_VISIBILITY_VISIBLE;
      setIsTabVisible(visible);

      if (visible && !shouldSkip) {
        refetch();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [refetch, shouldSkip]);

  return {
    callerPermissions,
    permissions,
    roles,
    highestPrecedenceRole: callerPermissions?.highestPrecedenceRole ?? null,
    hasAccess,
    isInitialLoading,
    isRefetching,
    error: shouldSkip ? undefined : error,
    refetch,
    networkStatus
  };
}

export default useMyPermissions;
